<template>
    <div class="flex items-center relative" :class="tabHeightClassName">
        <div
            class="scroll-hidden flex relative whitespace-no-wrap mr-2 overflow-x-auto | transition-all duration-200"
            :class="tabHeightClassName"
            ref="scrollBar"
        >
            <div
                class="bottom-0 rounded-t-lg shadow-lg absolute bg-white pointer-events-none"
                :class="[tabWidthClassName, tabHeightClassName, { 'transition-all duration-200 ease-in-out': mounted }]"
                :style="{ left: backgroundPosition }"
                ref="tabBackground"
            />
            <ActivixTab
                :class="[tabWidthClassName, tabHeightClassName]"
                :hide-separator="hideSeparator(tab)"
                :is-active="activeTab === tab.name"
                :is-disabled="tab.disabled"
                :text-alignment="textAlignment"
                :label="tab.label"
                :sub-label="tab.subLabel"
                :tooltip="tab.tooltip"
                :key="tab.name"
                @click="onTabClick(tab)"
                v-for="tab in tabs"
            >
                <slot :active-tab="activeTab" name="moreButtons" slot="moreButtons" />
            </ActivixTab>
            <div
                class="flex items-center px-6"
                :class="tabHeightClassName"
                v-if="$slots.after"
            >
                <slot name="after" />
            </div>
        </div>
    </div>
</template>

<script>
    import ActivixTab from './Tab.vue';
    import ActivixSpinner from '../Spinner/Spinner.vue';

    /**
     * Create a tabs navigation.
     * @displayName Tabs
     */
    export default {
        name: 'ActivixTabs',

        components: {
            ActivixTab,
            ActivixSpinner,
        },

        props: {
            tabs: {
                type: Array,
                required: true,
            },
            value: {
                required: true,
            },
            tabWidthClassName: {
                type: String,
                default: 'w-40',
            },
            textAlignment: {
                type: String,
                default: 'center',
            },
        },

        data: ({ value }) => ({
            activeTab: value,
            backgroundPosition: 0,
            mounted: false,
        }),

        computed: {
            hasSubLabel() {
                return this.tabs.some(tab => !!tab.subLabel);
            },

            tabHeightClassName() {
                return this.hasSubLabel ? 'h-16' : 'h-12'
            },

            currentTabIndex() {
                return this.tabs.findIndex(tab => tab.name === this.activeTab);
            },
        },

        watch: {
            currentTabIndex(tabIndex) {
                if (tabIndex === -1) {
                    this.resetActiveTab();
                } else {
                    this.setBackgroundPosition();
                }
            },

            activeTab(activeTab) {
                this.$emit('input', activeTab);
            },

            value(value) {
                if (this.activeTab !== value) {
                    this.activeTab = value;
                }
            },
        },

        methods: {
            hideSeparator(tab) {
                const index = this.tabs.findIndex(t => t.name === tab.name);

                if (index + 1 === this.currentTabIndex) {
                    return true;
                }

                return index === this.tabs.length - 1 && !this.$slots.after;
            },

            onTabClick(tab) {
                if (tab.disabled) {
                    return;
                }

                this.activeTab = tab.name;
            },

            setBackgroundPosition() {
                this.backgroundPosition = `${this.currentTabIndex * this.$refs.tabBackground.offsetWidth}px`;
            },

            resetActiveTab() {
                this.activeTab = this.tabs[0]?.name || null;
            },
        },

        mounted() {
            if (!this.value) {
                this.resetActiveTab();
            }

            this.setBackgroundPosition();

            setTimeout(() => {
                this.mounted = true;
            }, 0);
        },
    };
</script>

<style scoped>
    .scroll-hidden::-webkit-scrollbar {
        display: none;
        -ms-overflow-style: none;
        scrollbar-width: none;
        scrollbar-height: none;
    }
</style>
